/**
 * Where a signed-out client lands.
 *
 * This origin serves TWO applications: the public marketing site at `/` and the
 * authenticated portal at `/portal/*`. That single fact is what makes signing a
 * client out harder here than on the staff portal, where `/` IS the login page.
 *
 * Entra requires `post_logout_redirect_uri` to match a REGISTERED redirect URI,
 * and every URI registered on the CIAM app registration is a bare origin (no
 * path) — recorded in `.claude/rules/_project/portal-runtime-config.md`, read
 * from the directory 2026-08-29. So the logout round-trip can only ever return a
 * client to `/`, the marketing homepage, with nothing said about why they are
 * suddenly back on a brochure page and no sign-in prompt in front of them.
 *
 * This module carries them the last step: a one-shot breadcrumb written before
 * the logout redirect and consumed on the next boot, which sends them to
 * `/portal` — where `UnauthenticatedTemplate` renders the sign-in screen.
 *
 * It is the sign-OUT half of the defect `redirectStartPage: '/portal'` fixed for
 * the sign-IN half on 2026-08-27 ("when I logged in I still got the website not
 * even the portal"). Same origin, same cause, opposite direction.
 */

/** `sessionStorage`, not `localStorage`: this must not outlive the tab. */
export const SESSION_ENDED_KEY = 'babomrs.client.sessionEnded';

/**
 * `sessionStorage` throws rather than returning null in some privacy modes, and
 * an unreachable breadcrumb must degrade to "no redirect" — never to a crash on
 * a page a client is already struggling to reach.
 */
function store(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** `/portal` and `/portal/...` — but NOT `/portalling`. */
export function isPortalPath(pathname: string): boolean {
  return pathname === '/portal' || pathname.startsWith('/portal/');
}

/** Drop the breadcrumb immediately before handing over to the logout redirect. */
export function markSessionEnded(): void {
  try {
    store()?.setItem(SESSION_ENDED_KEY, '1');
  } catch {
    /* storage full or blocked — the client still gets signed out, just lands on `/` */
  }
}

/**
 * Read the breadcrumb and remove it in the same breath.
 *
 * One-shot BY CONSTRUCTION, which is the point: a flag that survived its read
 * would bounce the client off the marketing site every time they visited it for
 * the rest of the tab's life. Consuming on read means the redirect happens on
 * the first load after a sign-out and never again.
 */
export function consumeSessionEnded(): boolean {
  const s = store();
  if (!s) return false;
  try {
    const value = s.getItem(SESSION_ENDED_KEY);
    if (value !== null) s.removeItem(SESSION_ENDED_KEY);
    return value === '1';
  } catch {
    return false;
  }
}

/**
 * The landing decision, kept pure so it can be tested without a browser.
 *
 * Returns the path to send the client to, or `null` to leave them where they
 * are. Returning `null` for a client already inside `/portal/*` matters: they
 * are about to be shown the sign-in screen by the router anyway, and navigating
 * would throw away the deep link they arrived on for no gain.
 */
export function landingAfterSessionEnd(pathname: string, ended: boolean): string | null {
  if (!ended) return null;
  return isPortalPath(pathname) ? null : '/portal';
}
